import { useEffect } from "react";
import { useLocation } from "react-router";
import { useLocale, useTranslations } from "use-intl";
import { getPublicSiteUrl } from "./site-url";

const SEO_LD_SCRIPT_ID = "seo-structured-data";

type SeoRouteKey =
  | "home"
  | "privacy"
  | "terms"
  | "cookies"
  | "login"
  | "signup"
  | "reset_password"
  | "verify"
  | "staff_access"
  | "oauth"
  | "admin";

function resolveSeoRoute(pathname: string): SeoRouteKey {
  if (pathname.startsWith("/admin")) return "admin";
  const exact: Record<string, SeoRouteKey> = {
    "/": "home",
    "/privacy": "privacy",
    "/terms": "terms",
    "/cookies": "cookies",
    "/login": "login",
    "/signup": "signup",
    "/reset-password": "reset_password",
    "/verify": "verify",
    "/staff-access": "staff_access",
    "/oauth": "oauth",
  };
  return exact[pathname] ?? "home";
}

function localeToHtmlLang(locale: string): string {
  if (locale.startsWith("sv")) return "sv";
  return "en";
}

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  const selector =
    attr === "name"
      ? `meta[name="${key}"]`
      : `meta[property="${key}"]`;
  let el = document.head.querySelector(selector) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function removeMeta(attr: "name" | "property", key: string) {
  const selector =
    attr === "name"
      ? `meta[name="${key}"]`
      : `meta[property="${key}"]`;
  document.head.querySelector(selector)?.remove();
}

function setCanonical(href: string) {
  let link = document.head.querySelector(
    'link[rel="canonical"]',
  ) as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

function setJsonLdOnHome(baseUrl: string) {
  const existing = document.getElementById(SEO_LD_SCRIPT_ID);
  existing?.remove();
  const logoUrl = `${baseUrl}/favicon.svg`;
  const data = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        name: "Lojal",
        url: baseUrl,
        logo: logoUrl,
      },
      {
        "@type": "WebSite",
        name: "Lojal",
        url: baseUrl,
        publisher: { "@type": "Organization", name: "Lojal" },
      },
    ],
  };
  const script = document.createElement("script");
  script.id = SEO_LD_SCRIPT_ID;
  script.type = "application/ld+json";
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
}

function removeJsonLd() {
  document.getElementById(SEO_LD_SCRIPT_ID)?.remove();
}

export function SeoHead() {
  const { pathname } = useLocation();
  const locale = useLocale();
  const t = useTranslations("seo");
  const route = resolveSeoRoute(pathname);
  const title = t(`${route}.title`);
  const description = t(`${route}.description`);

  useEffect(() => {
    const base = getPublicSiteUrl().replace(/\/$/, "");
    const canonicalPath = pathname.split("?")[0] || "/";
    const canonicalHref =
      canonicalPath === "/"
        ? `${base}/`
        : `${base}${canonicalPath.endsWith("/") ? canonicalPath.slice(0, -1) : canonicalPath}`;

    document.documentElement.lang = localeToHtmlLang(locale);
    document.title = title;

    upsertMeta("name", "description", description);

    const isAdmin = pathname.startsWith("/admin");
    if (isAdmin) {
      upsertMeta("name", "robots", "noindex, nofollow");
    } else {
      removeMeta("name", "robots");
    }

    const ogImage = `${base}/og-image.png`;
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", canonicalHref);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:site_name", "Lojal");
    upsertMeta("property", "og:image", ogImage);

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", ogImage);

    setCanonical(canonicalHref);

    if (canonicalPath === "/") {
      setJsonLdOnHome(base);
    } else {
      removeJsonLd();
    }
  }, [pathname, locale, title, description]);

  return null;
}
